/* ═══════════════════════════════════════════════════════════════
   The Designer — node views for the non-prose blocks.

   Figures and smart tokens are single blocks in the flow. They render
   as the printed thing (the real image), never as a placeholder line,
   because the paper IS the editor.

   Tables are REAL TipTap table nodes as of §4 R7 (contract) — their
   NodeView (`SpecTableView`, visual-only: colgroup %, border/shading
   CSS vars) lives in tables.ts next to the rest of the table feature,
   wired in automatically by the framework's own columnResizing plugin.
   Nothing left to do here for tables.
   ═══════════════════════════════════════════════════════════════ */

import type { Node as PMNode } from '@tiptap/pm/model';
import type { NodeView } from '@tiptap/pm/view';

/** A figure, at the width it will print. */
export class SpecImageView implements NodeView {
  dom: HTMLElement;

  constructor(node: PMNode) {
    this.dom = document.createElement('div');
    this.dom.className = 'dsg-image-wrap';
    const data = node.attrs.imageData as string;
    if (data) {
      const img = document.createElement('img');
      img.src = data;
      img.alt = (node.attrs.text as string) || 'Figure';
      img.style.width = `${Math.max(20, Math.min(100, (node.attrs.imageWidth as number) ?? 100))}%`;
      this.dom.appendChild(img);
    } else {
      const fallback = document.createElement('div');
      fallback.className = 'dsg-atom-fallback';
      fallback.textContent = 'Figure — image data missing';
      this.dom.appendChild(fallback);
    }
  }

  stopEvent() {
    return false;
  }
}

/** A smart token: resolved content, read-only by design (authoring is cut
 *  from launch — §3.4). It reads as content, with a quiet chip so the writer
 *  knows it will refresh itself at export. */
export class SmartBlockView implements NodeView {
  dom: HTMLElement;

  constructor(node: PMNode) {
    this.dom = document.createElement('div');
    this.dom.className = 'dsg-smart';
    const body = document.createElement('span');
    body.className = 'dsg-smart-text';
    body.textContent = (node.attrs.text as string) || '';
    const chip = document.createElement('span');
    chip.className = 'dsg-smart-chip';
    const kind =
      ((node.attrs.meta as Record<string, unknown>)?.smartBlockKind as string) || 'smart block';
    chip.textContent = kind.replace(/-/g, ' ');
    chip.title = 'Smart block — recomputed from project data every time this spec is exported';
    this.dom.appendChild(body);
    this.dom.appendChild(chip);
  }

  stopEvent() {
    return false;
  }
}
